/**
 * Client mirror of api/src/utils/coordinates.js — keep the rules in step.
 *
 * Everything the map plots from an address (farm sites, distributor depots,
 * accommodation) lets someone type coordinates by hand to correct a bad
 * geocode. Hand-typed means fat fingers, so the same guards apply everywhere
 * rather than only on the site form, which is where they used to live alone.
 *
 * A bad pair is not cosmetic: accommodation's nearby-stays lists are distance
 * ranked off these numbers, and a site or depot with a wild coordinate drags
 * the map viewport across the world.
 */

const hasValue = (value) => value !== null && value !== undefined && value !== '';

/**
 * @param {string|number} lat
 * @param {string|number} lng
 * @param {{required?: boolean, noun?: string}} options
 *   required - the record cannot be saved without coordinates (accommodation,
 *              whose lat/lng columns are NOT NULL)
 *   noun     - what is being placed, for the 0,0 message
 * @returns {?string} an error message, or null when the pair is acceptable
 */
export function validateCoordinatePair(lat, lng, { required = false, noun = 'this record' } = {}) {
  const latSet = hasValue(lat);
  const lngSet = hasValue(lng);

  if (!latSet && !lngSet) {
    return required ? 'Latitude and longitude are required.' : null;
  }
  if (latSet && !lngSet) return 'Longitude is required when latitude is set.';
  if (!latSet && lngSet) return 'Latitude is required when longitude is set.';

  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    return 'Latitude must be between -90 and 90.';
  }
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    return 'Longitude must be between -180 and 180.';
  }
  // Null Island: the Atlantic off Ghana, and what a broken sensor or an empty
  // parse reports. Never a real address.
  if (latitude === 0 && longitude === 0) {
    return `Coordinates 0,0 are not valid for ${noun}.`;
  }

  return null;
}
